
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Popup from "../components/PopUP";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import TableFooter from "../components/TableFooter";
import DeleteModal from "../components/DeleteModal";
import api from "../Api";
import { IMAGE_URL, API_BASE_URL } from "../utils/constants";

const AIRCRAFT_LIST_URL =
  `${API_BASE_URL}/aircraft/aircraft-details/`;
const AIRCRAFT_UPLOAD_URL =
  `${API_BASE_URL}/aircraft/upload-json`;
const AIRCRAFT_STATUS_URL =
  `${API_BASE_URL}/aircraft/upload-json-status/`;
const AIRCRAFT_DELETE_URL =
  `${API_BASE_URL}/aircraft/delete-aircraft/`;
const AIRCRAFT_BULK_DELETE_URL =
  `${API_BASE_URL}/aircraft/bulk-delete-aircraft/`;
const AIRCRAFT_APPROVE_URL =
  `${API_BASE_URL}/aircraft/bulk-approve-aircraft/`;
const AIRCRAFT_DISAPPROVE_URL =
  `${API_BASE_URL}/aircraft/bulk-disapprove-aircraft/`;
const AIRCRAFT_UPDATE_URL =
  `${API_BASE_URL}/aircraft/update-aircraft/`;

const MANUFACTURER_LIST_URL =
  `${API_BASE_URL}/manufacturer/manufacturer-details/`;

const RECORDS_PER_PAGE = 15;

export default function UploadAircraftDetails() {
  const [aircrafts, setAircrafts] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);

  const [uploadMessage, setUploadMessage] = useState("");

  const [files, setFiles] = useState([]);
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [manufacturerSearch, setManufacturerSearch] = useState("");
  const [showManufacturerDropdown, setShowManufacturerDropdown] =
    useState(false);

  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [loadingManufacturers, setLoadingManufacturers] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const [hasUploaded, setHasUploaded] = useState(false);

  const [popupMessage, setPopupMessage] = useState("");
  const [apiError, setApiError] = useState("");

  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [uploadTaskId, setUploadTaskId] = useState(null);

  const [pagination, setPagination] = useState({
    count: 0,
    total_pages: 0,
    next: null,
    previous: null,
  });

  const recordsPerPage = RECORDS_PER_PAGE;

  // FETCH MANUFACTURERS FOR DROPDOWN
  const fetchManufacturers = useCallback(async () => {
    try {
      setLoadingManufacturers(true);

      const response = await api.get(MANUFACTURER_LIST_URL, {
        withCredentials: true,
      });

      const data = response.data;
      const records = Array.isArray(data)
        ? data
        : Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data?.data)
            ? data.data
            : [];

      setManufacturers(records);
    } catch (error) {
      console.error("Manufacturer fetch error:", error);
      // setApiError(
      //   error.response?.data?.detail ||
      //     "Failed to fetch manufacturers."
      // );
    } finally {
      setLoadingManufacturers(false);
    }
  }, []);

  // FETCH AIRCRAFT LIST
  const fetchAircrafts = useCallback(async (page = 1, search = "") => {
  try {
    setListLoading(true);

    const response = await api.get(AIRCRAFT_LIST_URL, {
      params: {
        page,
        q: search.trim(),
      },
      withCredentials: true,
    });

    const data = response.data;
    const records = Array.isArray(data?.results)
      ? data.results
      : Array.isArray(data)
        ? data
        : [];

    setAircrafts(records);

    setPagination({
      count: data?.count ?? records.length,
      total_pages: data?.total_pages ?? 1,
      next: data?.next ?? null,
      previous: data?.previous ?? null,
    });

    setApiError("");
  } catch (error) {
    const message =
      error.response?.data?.detail ||
      "Failed to fetch aircraft details.";

    setApiError(message);
    setPopupMessage(
      typeof message === "string"
        ? message
        : "Failed to fetch aircraft details."
    );
  } finally {
    setListLoading(false);
  }
  }, []);

  useEffect(() => {
    fetchManufacturers();
  }, [fetchManufacturers]);


  useEffect(() => {
    const timer = setTimeout(() => {
      fetchManufacturers(currentPage);
    }, 400);

    return () => clearTimeout(timer);
  }, [fetchManufacturers, currentPage]);


  useEffect(() => {
    setCurrentPage(1);
  }, [query]);

  // MANUFACTURER DROPDOWN SEARCH
  const filteredManufacturers = useMemo(() => {
    const search = manufacturerSearch.trim().toLowerCase();

    if (!search) return manufacturers;

    return manufacturers.filter((manufacturer) =>
      (manufacturer.company_name || "")
        .toLowerCase()
        .includes(search)
    );
  }, [manufacturers, manufacturerSearch]);

  // SEARCH AIRCRAFT

  const filteredAircrafts = aircrafts;
  const totalPages = Math.max(1, pagination.total_pages || 1);
  const paginatedAircrafts = aircrafts;

  useEffect(() => {
    setCurrentPage(1);
  }, [query]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // FILE SELECTION: JSON FILES ONLY
  
  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);

    setApiError("");
    setPopupMessage("");
    setUploadMessage("");

    const validFiles = selectedFiles.filter((file) =>
      file.name.toLowerCase().endsWith(".json")
    );

    const invalidFiles = selectedFiles.filter(
      (file) => !file.name.toLowerCase().endsWith(".json")
    );

    if (invalidFiles.length > 0) {
      setApiError(
        invalidFiles
          .map((file) => `${file.name}: Only JSON files are allowed`)
          .join("\n")
      );
    }

    setFiles((previous) => {
      const combined = [...previous, ...validFiles];

      const unique = new Map(
        combined.map((file) => [
          `${file.name}-${file.size}-${file.lastModified}`,
          file,
        ])
      );

      return Array.from(unique.values());
    });

    event.target.value = "";
  };


  const removeFile = (indexToRemove) => {
    setFiles((previous) =>
      previous.filter((_, index) => index !== indexToRemove)
    );
  };

  // UPLOAD STATUS POLLING
  
const checkUploadStatus = async (taskId) => {
  try {
    const response = await api.get(
      `${AIRCRAFT_STATUS_URL}${taskId}`,
      { withCredentials: true }
    );

    const data = response.data;
    const status = String(data?.status || "").toLowerCase();

    if (status === "completed" || status === "failed") {
      setUploadTaskId(null);
      setLoading(false);
      setFiles([]);
      setHasUploaded(true);
      setSelectedIds([]);

      const results = Array.isArray(data?.results)
        ? data.results
        : [];

      const successfulFiles = results.filter(
        (item) => item.status === "success"
      );

      const failedFiles = results.filter(
        (item) => item.status === "failed"
      );

      // Failed file details red text mein dikhengi.
      if (failedFiles.length > 0 || status === "failed") {
        const errorDetails = failedFiles.map(
          (item) =>
            `${item.file_name}: ${item.message || "Upload failed."}`
        );

        setUploadMessage(
          [
            // `Successful files: ${
            //   data.successful_files ?? successfulFiles.length
            // }`,
            // `Failed files: ${
            //   data.failed_files ?? failedFiles.length
            // }`,
            ...errorDetails,
            ...(status === "failed" && errorDetails.length === 0
              ? [data.message || "Aircraft upload failed."]
              : []),
          ].join("\n")
        );
      } else {
        setUploadMessage("");
      }

      // Popup sirf successful upload par.
      if (successfulFiles.length > 0 && status !== "failed") {
        setPopupMessage(
          data.message || "Aircraft files uploaded successfully."
        );
      } else {
        setPopupMessage("");
      }

      await fetchAircrafts();
      return;
    }

    window.setTimeout(() => {
      checkUploadStatus(taskId);
    }, 2000);
  } catch (error) {
    console.error("Aircraft upload status error:", error);

    const detail = error.response?.data?.detail;

    setApiError(
      typeof detail === "string"
        ? detail
        : detail?.message ||
            "Failed to check aircraft upload status."
    );

    setPopupMessage("");
    setUploadTaskId(null);
    setLoading(false);
  }
};


  // UPLOAD MULTIPLE JSON FILES WITH MANUFACTURER ID
  const handleSubmit = async (event) => {
    event.preventDefault();

    setApiError("");
    setPopupMessage("");
    setUploadMessage("");

    if (!selectedManufacturer?.id) {
      setApiError("Please select a manufacturer.");
      return;
    }

    if (files.length === 0) {
      setApiError("Please select at least one JSON file.");
      return;
    }

    if (uploadTaskId) {
      setApiError("An aircraft upload is already processing.");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append(
        "manufacturer_id",
        String(selectedManufacturer.id)
      );

      files.forEach((file) => {
        formData.append("files", file);
      });

      const response = await api.post(
        AIRCRAFT_UPLOAD_URL,
        formData,
        { withCredentials: true }
      );

      const data = response.data;

      if (data?.task_id) {
        setUploadTaskId(data.task_id);
        setPopupMessage("");

        await checkUploadStatus(data.task_id);
        return;
      }

      setFiles([]);
      setLoading(false);
      setPopupMessage(
        data.message ||
          data.detail ||
          `Aircraft files uploaded successfully. Inserted: ${
            data.inserted_aircrafts ??
            data.inserted_records ??
            data.inserted ??
            0
          }`
      );

      await fetchAircrafts();

    } catch (error) {
      console.error(
        "Aircraft upload error:",
        error.response?.data || error
      );

      const detail = error.response?.data?.detail;

      const errorMessage = Array.isArray(detail)
        ? detail
            .map((item) =>
              typeof item === "string"
                ? item
                : item?.msg || JSON.stringify(item)
            )
            .join("\n")
        : typeof detail === "string"
          ? detail
          : detail?.message ||
            error.response?.data?.message ||
            "Failed to upload aircraft files.";

      setApiError(errorMessage);
      setPopupMessage("");
      setLoading(false);
    }

  };

  // SELECT / DESELECT AIRCRAFT
  const handleSelectAircraft = (id) => {
    setSelectedIds((previous) =>
      previous.some((selectedId) => String(selectedId) === String(id))
        ? previous.filter(
            (selectedId) => String(selectedId) !== String(id)
          )
        : [...previous, id]
    );
  };

  const visibleIds = paginatedAircrafts.map(
    (aircraft) => aircraft.id
  );

  const allVisibleSelected =
    visibleIds.length > 0 &&
    visibleIds.every((id) =>
      selectedIds.some(
        (selectedId) => String(selectedId) === String(id)
      )
    );

  const handleSelectAll = (checked) => {
    setSelectedIds((previous) =>
      checked
        ? [
            ...previous,
            ...visibleIds.filter(
              (id) =>
                !previous.some(
                  (selectedId) => String(selectedId) === String(id)
                )
            ),
          ]
        : previous.filter(
            (id) =>
              !visibleIds.some(
                (visibleId) => String(visibleId) === String(id)
              )
          )
    );
  };

  // BULK APPROVE / DISAPPROVE
  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) {
      setPopupMessage("Please select at least one aircraft.");
      return;
    }

    const endpoint =
      action === "approve"
        ? AIRCRAFT_APPROVE_URL
        : AIRCRAFT_DISAPPROVE_URL;

    const idsToUpdate = [...selectedIds];

    try {
      setBulkActionLoading(true);
      setApiError("");

      const response = await api.patch(
        endpoint,
        { aircraft_ids: idsToUpdate },
        { withCredentials: true }
      );

      setAircrafts((previous) =>
        previous.map((aircraft) =>
          idsToUpdate.some(
            (id) => String(id) === String(aircraft.id)
          )
            ? {
                ...aircraft,
                is_approved: action === "approve",
              }
            : aircraft
        )
      );

      setSelectedIds([]);

      setPopupMessage(
        response.data?.message ||
          response.data?.detail ||
          `${idsToUpdate.length} aircraft(s) ${
            action === "approve" ? "approved" : "disapproved"
          } successfully.`
      );
    } catch (error) {
      console.error(`Aircraft bulk ${action} error:`, error);

      const detail = error.response?.data?.detail;

      setPopupMessage(
        typeof detail === "string"
          ? detail
          : error.response?.data?.message ||
              `Failed to ${action} selected aircraft.`
      );
    } finally {
      setBulkActionLoading(false);
    }
  };

  // OPTIONAL SINGLE AIRCRAFT APPROVAL TOGGLE
  const handleAircraftApprovalToggle = async (aircraft) => {
    try {
      setBulkActionLoading(true);

      const response = await api.patch(
        AIRCRAFT_UPDATE_URL,
        { aircraft_id: aircraft.id },
        { withCredentials: true }
      );

      setPopupMessage(
        response.data?.message ||
          "Aircraft approval updated."
      );

      await fetchAircrafts();
    } catch (error) {
      console.error("Aircraft approval update error:", error);

      setPopupMessage(
        error.response?.data?.detail ||
          "Failed to update aircraft approval."
      );
    } finally {
      setBulkActionLoading(false);
    }
  };

  // OPEN DELETE MODAL
  const handleDeleteClick = (aircraft) => {
    setSelectedAircraft(aircraft);
    setShowModal(true);
  };

  const handleBulkDeleteClick = () => {
    if (selectedIds.length === 0) {
      setPopupMessage("Please select at least one aircraft.");
      return;
    }

    setSelectedAircraft(null);
    setShowModal(true);
  };

  // SINGLE DELETE AND BULK DELETE
  const handleConfirmDelete = async () => {
    const isSingleDelete = Boolean(selectedAircraft?.id);
    const idsToDelete = isSingleDelete
      ? [selectedAircraft.id]
      : [...selectedIds];

    if (idsToDelete.length === 0) return;

    try {
      setLoading(true);
      setApiError("");

      let response;

      if (isSingleDelete) {
        response = await api.delete(AIRCRAFT_DELETE_URL, {
          data: { aircraft_id: selectedAircraft.id },
          withCredentials: true,
        });
      } else {
        response = await api.delete(AIRCRAFT_BULK_DELETE_URL, {
          data: { aircraft_ids: idsToDelete },
          withCredentials: true,
        });
      }

      setSelectedIds((previous) =>
        previous.filter(
          (id) =>
            !idsToDelete.some(
              (deletedId) => String(id) === String(deletedId)
            )
        )
      );

      setShowModal(false);
      setSelectedAircraft(null);

      setPopupMessage(
        response.data?.message ||
          response.data?.detail ||
          (isSingleDelete
            ? "Aircraft deleted successfully."
            : "Aircraft records deleted successfully.")
      );

      await fetchAircrafts();
    } catch (error) {
      console.error("Aircraft delete error:", error);

      const detail = error.response?.data?.detail;

      setPopupMessage(
        typeof detail === "string"
          ? detail
          : error.response?.data?.message ||
              "Failed to delete aircraft."
      );
    } finally {
      setLoading(false);
    }
  };

  const getAircraftName = (aircraft) =>
    aircraft.Aircraft_Model ||
    aircraft.aircraft_model ||
    aircraft.model ||
    aircraft.name ||
    "-";

  const getAircraftImage = (aircraft) => {
    const images = aircraft.images;

    if (Array.isArray(images)) {
      const defaultImage = images.find((image) => image?.is_default);
      return defaultImage?.url || images[0]?.url || "";
    }

    return typeof images === "string" ? images : "";
  };

  return (
    <div className="user-management-page">
      <Popup
        message={popupMessage}
        onClose={() => setPopupMessage("")}
      />

      <Sidebar />

      <div className="main-wrapper">
        <Header />

        <div className="content-wrapper">
          <section className="main-section spacer-y">
            <div className="container">
              <div className="row gy-4">
                <div className="col-xl-12">
                  <div className="common_main_bg_wrapper">
                    <div className="common_main_heading_wrapper">
                      <div className="vstack">
                        <nav className="breadcrumb_nav">
                          <ul className="breadcrumb mb-0" />
                        </nav>

                        <h1>Aircraft Ingestion</h1>
                      </div>
                    </div>
                  </div>

                  {/* AIRCRAFT JSON UPLOAD */}
                  <form onSubmit={handleSubmit}>
                    <div className="row common-outer-card mx-2 my-5 p-3 justify-content-center">
                      <div className="col-12 col-md-6 col-lg-5">
                        <div className="form_field">
                          <label>Select Manufacturer</label>

                          <div className="input_field">
                            <input
                              type="text"
                              className="form-control"
                              placeholder={
                                selectedManufacturer
                                  ? selectedManufacturer.company_name
                                  : loadingManufacturers
                                    ? "Loading manufacturers..."
                                    : "Search manufacturer"
                              }
                              value={manufacturerSearch}
                              onChange={(event) => {
                                setManufacturerSearch(event.target.value);
                                setShowManufacturerDropdown(true);
                                setSelectedManufacturer(null);
                              }}
                              onFocus={() =>
                                setShowManufacturerDropdown(true)
                              }
                              disabled={loadingManufacturers || loading}
                            />

                            {showManufacturerDropdown && (
                              <div
                                className="border rounded bg-white mt-1"
                                style={{
                                  maxHeight: "200px",
                                  overflowY: "auto",
                                  position: "relative",
                                  zIndex: 10,
                                }}
                              >
                                {filteredManufacturers.length > 0 ? (
                                  filteredManufacturers.map((manufacturer) => (
                                    <button
                                      type="button"
                                      key={manufacturer.id}
                                      className="dropdown-item text-start px-3 py-2"
                                      onClick={() => {
                                        setSelectedManufacturer(manufacturer);
                                        setManufacturerSearch(
                                          manufacturer.company_name || ""
                                        );
                                        setShowManufacturerDropdown(false);
                                      }}
                                    >
                                      {manufacturer.company_name}
                                    </button>
                                  ))
                                ) : (
                                  <div className="px-3 py-2 text-muted">
                                    No manufacturers found.
                                  </div>
                                )}
                              </div>
                            )}

                            {/* {selectedManufacturer && (
                              <button
                                type="button"
                                className="btn btn-sm btn-link px-0"
                                onClick={() => {
                                  setSelectedManufacturer(null);
                                  setManufacturerSearch("");
                                }}
                              >
                                Clear manufacturer
                              </button>
                            )} */}
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-md-6 col-lg-5">
                        <div className="form_field">
                          <label>Upload JSON files here</label>

                          <div className="input_field">
                            <input
                              id="aircraft-upload-files"
                              type="file"
                              className="form-control"
                              name="files"
                              accept=".json,application/json"
                              multiple
                              onChange={handleFileChange}
                              disabled={loading}
                            />

                            <span
                              className="error"
                              style={{ whiteSpace: "pre-line", display: "block" }}
                            >
                              {apiError}
                            </span>

                            {uploadMessage && (
                              <div
                                style={{
                                  color: "red",
                                  whiteSpace: "pre-line",
                                  marginTop: "8px",
                                  fontSize: "12px",
                                }}
                              >
                                {uploadMessage}
                              </div>
                            )}

                          </div>

                          {files.length > 0 && (
                            <div className="mt-3">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <strong>
                                  Selected files ({files.length})
                                </strong>

                                <button
                                  type="button"
                                  className="btn btn-sm btn-link"
                                  onClick={() => setFiles([])}
                                  disabled={loading}
                                >
                                  Clear all
                                </button>
                              </div>

                              <div className="border rounded p-2">
                                {files.map((file, index) => (
                                  <div
                                    key={`${file.name}-${file.lastModified}-${index}`}
                                    className="d-flex align-items-center justify-content-between gap-2 py-1"
                                  >
                                    <span
                                      className="text-truncate"
                                      title={file.name}
                                    >
                                      {file.name}
                                    </span>

                                    <button
                                      type="button"
                                      className="btn btn-sm btn-link text-danger"
                                      onClick={() => removeFile(index)}
                                      disabled={loading}
                                      aria-label={`Remove ${file.name}`}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-12 col-md-6 col-lg-2 mt-3">
                        <button
                          type="submit"
                          className="secondary_btn mt-3"
                          disabled={
                            loading ||
                            !selectedManufacturer ||
                            files.length === 0
                          }
                        >
                          {loading ? "Uploading..." : "Upload"}
                        </button>
                      </div>
                    </div>
                  </form>

                  {/* AIRCRAFT LISTING */}
                  {hasUploaded && (
                    <div className="common-table-wrapper mt-4">
                      <div className="common-table-filter-wrapper">
                        <div className="common-left-blk">
                          <div className="common_sub_heading_wrapper mb-0">
                            <h2>Uploaded Aircraft Listing</h2>

                            <div className="d-flex flex-wrap gap-3 mt-2">
                              <span>
                                {/* Total: <strong>{pagination.count}</strong> */}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="common-right-blk">
                          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                            <button
                              type="button"
                              className="secondary_btn"
                              onClick={() => handleBulkAction("approve")}
                              disabled={
                                bulkActionLoading ||
                                selectedIds.length === 0
                              }
                            >
                              {bulkActionLoading ? "Please wait..." : "Approve"}
                            </button>

                            <button
                              type="button"
                              className="secondary_btn"
                              onClick={() => handleBulkAction("disapprove")}
                              disabled={
                                bulkActionLoading ||
                                selectedIds.length === 0
                              }
                            >
                              Disapprove
                            </button>

                            <button
                              type="button"
                              className="secondary_btn"
                              onClick={handleBulkDeleteClick}
                              disabled={
                                bulkActionLoading ||
                                loading ||
                                selectedIds.length === 0
                              }
                            >
                              Delete
                            </button>
                          </div>

                          <div className="common-sort-blk">
                            <div className="common-search-blk">
                              <input
                                type="search"
                                className="form-control"
                                placeholder="Search aircraft"
                                value={query}
                                onChange={(event) =>
                                  setQuery(event.target.value)
                                }
                                name="aircraft_search"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {loading || listLoading ? (
                        <div
                          className="d-flex justify-content-center align-items-center"
                          style={{ minHeight: "200px" }}
                        >
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">
                              Loading...
                            </span>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="table-blk table-responsive">
                            <table className="table">
                              <thead>
                                <tr>
                                  <th>
                                    <input
                                      type="checkbox"
                                      aria-label="Select all aircraft on this page"
                                      checked={allVisibleSelected}
                                      onChange={(event) =>
                                        handleSelectAll(event.target.checked)
                                      }
                                    />
                                  </th>
                                  <th>S.No.</th>
                                  <th>Aircraft Model</th>
                                  <th>Image</th>
                                  <th>Manufacturer</th>
                                  <th>Status</th>
                                  <th>Action</th>
                                </tr>
                              </thead>

                              <tbody>
                                {paginatedAircrafts.length > 0 ? (
                                  paginatedAircrafts.map((aircraft, index) => {
                                    const imageUrl = getAircraftImage(aircraft);
                                    const manufacturerName =
                                      aircraft.manufacturer?.company_name ||
                                      aircraft.manufacturer_name ||
                                      manufacturers.find(
                                        (manufacturer) =>
                                          String(manufacturer.id) ===
                                          String(aircraft.manufacturer_id)
                                      )?.company_name ||
                                      "-";

                                    return (
                                      <tr key={aircraft.id}>
                                        <td>
                                          <input
                                            type="checkbox"
                                            aria-label={`Select ${getAircraftName(
                                              aircraft
                                            )}`}
                                            checked={selectedIds.some(
                                              (id) =>
                                                String(id) ===
                                                String(aircraft.id)
                                            )}
                                            onChange={() =>
                                              handleSelectAircraft(aircraft.id)
                                            }
                                          />
                                        </td>

                                        <td>
                                          {(currentPage - 1) *
                                            recordsPerPage +
                                            index +
                                            1}
                                        </td>

                                        <td>
                                          {getAircraftName(aircraft)}
                                        </td>

                                        <td>
                                          {imageUrl ? (
                                            <img
                                              src={imageUrl}
                                              alt={getAircraftName(aircraft)}
                                              style={{
                                                width: "55px",
                                                height: "45px",
                                                objectFit: "contain",
                                              }}
                                              onError={(event) => {
                                                event.currentTarget.style.display =
                                                  "none";
                                              }}
                                            />
                                          ) : (
                                            "-"
                                          )}
                                        </td>

                                        <td>{manufacturerName}</td>

                                        <td>
                                          {aircraft.is_approved ? (
                                            <span className="badge bg-light text-success">
                                              Active
                                            </span>
                                          ) : (
                                            <span className="badge bg-light text-danger">
                                              Inactive
                                            </span>
                                          )}
                                        </td>

                                        <td>
                                          <button
                                            type="button"
                                            className="border-0 bg-white"
                                            onClick={() =>
                                              handleDeleteClick(aircraft)
                                            }
                                            aria-label={`Delete ${getAircraftName(
                                              aircraft
                                            )}`}
                                            disabled={loading}
                                          >
                                            <img
                                              src={IMAGE_URL + "trash-icon.svg"}
                                              alt="Trash"
                                            />
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })
                                ) : (
                                  <tr>
                                    <td colSpan={7} className="text-center">
                                      {apiError || "No aircraft records found."}
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>

                          <TableFooter
                            pagination={{
                              count: pagination.count,
                              total_pages: pagination.total_pages,
                              next: pagination.next,
                              previous: pagination.previous,
                            }}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                          />
                        </>
                      )}
                    </div>
                  )}
                  

                  <DeleteModal
                    show={showModal}
                    onClose={() => {
                      setShowModal(false);
                      setSelectedAircraft(null);
                    }}
                    onConfirm={handleConfirmDelete}
                    itemName={
                      selectedAircraft
                        ? getAircraftName(selectedAircraft)
                        : `${selectedIds.length} selected aircraft`
                    }
                  />
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
